import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Globe, User, Search } from 'lucide-react';
import { useUserAuth } from '../../context/userAuthContext';
import { Link } from 'react-router-dom'; // Asumiendo que usas React Router

const translations = {
  en: {
    products: 'Products',
    solutions: 'Solutions',
    industries: 'Industries',
    forYou: 'For You',
    buy: 'Buy',
    drivers: 'Drivers',
    support: 'Support',
    english: 'English',
    spanish: 'Spanish',
    search: 'Search',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    myAccount: 'My Account',
    profile: 'Profile'
  },
  es: {
    products: 'Productos',
    solutions: 'Soluciones',
    industries: 'Industrias',
    forYou: 'Para Usted',
    buy: 'Comprar',
    drivers: 'Controladores',
    support: 'Soporte',
    english: 'Inglés',
    spanish: 'Español',
    search: 'Buscar',
    signIn: 'Iniciar Sesión',
    signOut: 'Cerrar Sesión',
    myAccount: 'Mi Cuenta',
    profile: 'Perfil'
  }
};

const FroniusNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Usamos el contexto unificado para obtener idioma y funciones de autenticación
  const { currentLanguage, changeLanguage, user, logOut } = useUserAuth();

  const t = translations[currentLanguage];

  // Detectar scroll para cambiar estilo del navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Cierra otros menús si están abiertos
    if (isLanguageMenuOpen) setIsLanguageMenuOpen(false);
    if (isUserMenuOpen) setIsUserMenuOpen(false);
  };

  const toggleLanguageMenu = (e) => {
    e.stopPropagation(); // Evita que el clic se propague
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
    if (isUserMenuOpen) setIsUserMenuOpen(false);
  };

  const toggleUserMenu = (e) => {
    e.stopPropagation(); // Evita que el clic se propague
    setIsUserMenuOpen(!isUserMenuOpen);
    if (isLanguageMenuOpen) setIsLanguageMenuOpen(false);
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsLanguageMenuOpen(false);
  };

  // Cierra los menús si se hace clic fuera de ellos
  useEffect(() => {
    const handleClickOutside = () => {
      if (isLanguageMenuOpen) setIsLanguageMenuOpen(false);
      if (isUserMenuOpen) setIsUserMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLanguageMenuOpen, isUserMenuOpen]);

  return (
    <>
      {/* Main Navbar - Sticky */}
      <nav className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-white shadow-md py-2" : "bg-white/95 py-3"
        }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Left section - Logo and desktop menu */}
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button onClick={toggleMenu} className="p-1 mr-3 lg:hidden">
                <Menu className="h-6 w-6 text-gray-700" />
              </button>

              {/* Logo clickeable */}
              <Link to="/" className="flex items-center mr-8 cursor-pointer">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/logos%2Flogofroniostransparente-removebg-preview.png?alt=media&token=ef4a037a-358f-4b67-86e6-31cf73b0c576"
                  alt="Fronios Logo"
                  className="h-8 w-auto"
                />
                <span className="font-sans tracking-tight text-black ml-2">
                  <span className="text-2xl font-light">fronios</span>
                </span>
              </Link>

              {/* Desktop navigation - Hidden on mobile */}
              <div className="hidden lg:flex items-center space-x-6">
                <div className="relative group">
                  <button className="flex items-center text-gray-700 hover:text-blue-500 py-2">
                    <span>{t.products}</span>
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-10">
                    <div className="p-4 space-y-3">
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Product 1</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Product 2</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Product 3</a>
                    </div>
                  </div>
                </div>
                <div className="relative group">
                  <button className="flex items-center text-gray-700 hover:text-blue-500 py-2">
                    <span>{t.solutions}</span>
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-10">
                    <div className="p-4 space-y-3">
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Solution 1</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Solution 2</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Solution 3</a>
                    </div>
                  </div>
                </div>
                <div className="relative group">
                  <button className="flex items-center text-gray-700 hover:text-blue-500 py-2">
                    <span>{t.industries}</span>
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-10">
                    <div className="p-4 space-y-3">
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Industry 1</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Industry 2</a>
                      <a href="#" className="block text-gray-700 hover:text-blue-500">Industry 3</a>
                    </div>
                  </div>
                </div>
                <a href="#" className="text-gray-700 hover:text-blue-500 py-2">{t.support}</a>
              </div>
            </div>

            {/* Right section - Search, Language, User */}
            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Search - Hidden on small mobile */}
              <button className="p-1 hidden sm:block">
                <Search className="h-5 w-5 text-gray-700" />
              </button>

              {/* Language selector */}
              <div className="relative">
                <button
                  className="p-1 flex items-center"
                  onClick={toggleLanguageMenu}
                >
                  <Globe className="h-5 w-5 text-gray-700" />
                </button>

                {/* Dropdown para seleccionar idioma */}
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg z-50">
                    <div className="py-1" role="menu">
                      <button
                        className={`flex items-center px-4 py-2 text-sm w-full text-left ${currentLanguage === 'en' ? 'bg-blue-50 text-blue-500' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        onClick={() => handleLanguageChange('en')}
                      >
                        <span className={`w-3 h-3 rounded-full mr-2 ${currentLanguage === 'en' ? 'bg-blue-500' : 'bg-transparent border border-gray-300'}`}></span>
                        {t.english}
                      </button>
                      <button
                        className={`flex items-center px-4 py-2 text-sm w-full text-left ${currentLanguage === 'es' ? 'bg-blue-50 text-blue-500' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        onClick={() => handleLanguageChange('es')}
                      >
                        <span className={`w-3 h-3 rounded-full mr-2 ${currentLanguage === 'es' ? 'bg-blue-500' : 'bg-transparent border border-gray-300'}`}></span>
                        {t.spanish}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User menu */}
              <div className="relative">
                <button className="p-1" onClick={toggleUserMenu}>
                  <User className="h-5 w-5 text-gray-700" />
                </button>

                {/* User dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                    <div className="py-1" role="menu">
                      {user ? (
                        <>
                          <div className="px-4 py-2 border-b border-gray-100">
                            <p className="text-sm font-medium text-gray-800">{user.email}</p>
                          </div>
                          <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            {t.profile}
                          </a>
                          <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            {t.myAccount}
                          </a>
                          <button
                            onClick={() => {
                              logOut();
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            {t.signOut}
                          </button>
                        </>
                      ) : (
                        <a href="/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          {t.signIn}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Placeholder para compensar el navbar fijo */}
      <div className={`${isScrolled ? "h-16" : "h-20"} transition-all duration-300`}></div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto pt-16">
          <div className="p-4">
            {/* Close Button */}
            <button
              onClick={toggleMenu}
              className="absolute top-4 left-4 p-1"
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>

            {/* Logo en el menú móvil */}
            <div className="flex justify-center mb-8">
              <Link to="/" className="flex items-center" onClick={toggleMenu}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/logos%2Flogofroniostransparente-removebg-preview.png?alt=media&token=ef4a037a-358f-4b67-86e6-31cf73b0c576"
                  alt="Fronios Logo"
                  className="h-8 w-auto"
                />
                <span className="font-sans tracking-tight text-black ml-2">
                  <span className="text-2xl font-light">fronios</span>
                </span>
              </Link>
            </div>

            {/* Menu Items - Traducidos según el idioma seleccionado */}
            <div className="space-y-5 mt-4">
              <div className="border-b border-gray-200 pb-5">
                <button className="w-full flex items-center justify-between py-2 text-gray-800">
                  <span className="text-lg">{t.products}</span>
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="border-b border-gray-200 pb-5">
                <button className="w-full flex items-center justify-between py-2 text-gray-800">
                  <span className="text-lg">{t.solutions}</span>
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="border-b border-gray-200 pb-5">
                <button className="w-full flex items-center justify-between py-2 text-gray-800">
                  <span className="text-lg">{t.industries}</span>
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="border-b border-gray-200 pb-5">
                <button className="w-full flex items-center justify-between py-2 text-gray-800">
                  <span className="text-lg">{t.forYou}</span>
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="border-b border-gray-200 pb-5">
                <button className="w-full text-left py-2 text-gray-800 font-medium">
                  <span className="text-lg">{t.buy}</span>
                </button>
              </div>

              <div className="border-b border-gray-200 pb-5">
                <button className="w-full text-left py-2 text-gray-800">
                  <span className="text-lg">{t.drivers}</span>
                </button>
              </div>

              <div className="pb-5">
                <button className="w-full text-left py-2 text-gray-800">
                  <span className="text-lg">{t.support}</span>
                </button>
              </div>

              {/* Opciones de usuario si está autenticado */}
              {user && (
                <div className="border-t border-gray-200 pt-5 mt-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">{user.email}</span>
                  </div>
                  <button
                    onClick={() => logOut()}
                    className="w-full text-left py-2 text-red-600 hover:text-red-800"
                  >
                    <span className="text-lg">{t.signOut}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FroniusNavbar;